import type { HousekeepingState, OccupancyState, ServiceState } from '../state/roomState'
import type { TaskState } from './work'
import { request } from './client'

/* The board read model, as the server sends it.

   The state fields are typed as the DESIGN SYSTEM's unions, not as bare strings. That
   is deliberate: the server's wire vocabulary (Housekeeping.Application.Common.Wire)
   was written to match these exact spellings, and typing them this way means a wire
   value the design system does not know about is a compile error here rather than a
   blank tile in Najaf. */

/** A building, with both names so the board can draw either without a second call. */
export interface BoardBuilding {
  buildingId: string
  code: string
  name: string
  nameAr: string | null
  sortOrder: number
}

export interface BoardFloor {
  floorId: string
  /** Which building this floor is in. The board groups on it. */
  buildingId: string
  code: string
  sortOrder: number
}

export interface BoardRoom {
  roomId: string
  roomNumber: string
  floorId: string
  roomType: string
  roomTypeAr: string | null
  housekeeping: HousekeepingState
  occupancy: OccupancyState
  service: ServiceState
  assignedToUserId: string | null
  assignedToName: string | null
  departureUtc: string | null
}

/**
 * NOTE WHAT IS ABSENT: no `isReady` and no `notReadyReason`.
 *
 * Readiness is derived on the client, by the same rule the handset applies offline.
 * A server-sent answer alongside it would be a second value for one fact, and the two
 * would disagree by one refresh interval — a tile whose colour contradicts the chip
 * counting it. `requireInspection` is the one piece of server state the rule needs.
 */
export interface RoomBoardView {
  propertyId: string
  requireInspection: boolean
  generatedAtUtc: string
  /** Already ordered. `floors` is ordered by building and then by floor, so the board
   *  can draw both lists in the order they arrive rather than re-deriving an order that
   *  needs a field the floor row does not carry. */
  buildings: BoardBuilding[]
  floors: BoardFloor[]
  rooms: BoardRoom[]
}

export function fetchRoomBoard(
  propertyId: string,
  options: {
    floorId?: string
    search?: string
    buildingId?: string
    signal?: AbortSignal
  } = {},
): Promise<RoomBoardView> {
  const params = new URLSearchParams()
  if (options.floorId) params.set('floorId', options.floorId)
  if (options.search) params.set('search', options.search)
  if (options.buildingId) params.set('buildingId', options.buildingId)
  const query = params.toString()

  return request<RoomBoardView>(
    `/properties/${propertyId}/rooms/board${query ? `?${query}` : ''}`,
    options.signal ? { signal: options.signal } : {},
  )
}

/* ── MOVING A ROOM BY HAND ─────────────────────────────────────────────────────

   THE FIRST CLIENT THIS ENDPOINT HAS EVER HAD. It was written in Sprint 1 and nothing
   called it for thirty-four; Sprint 35 gives it a caller, and in the same breath the
   server stopped taking `{"toState":3}` and started taking the word.

   **ONLY ONE TRANSITION BELONGS ON A SCREEN, AND IT IS THE OVERRIDE.** The endpoint moves
   a room and nothing else. Eight of the nine transitions in the matrix are the TASK
   workflow — an attendant starting, submitting, a supervisor accepting, rejecting,
   cancelling — and every one of them has an endpoint of its own that moves the room AND
   updates the task. Reaching them through here would move the room while leaving the task
   where it was: a handset still showing a clean in progress on a room the board now calls
   inspected, and no screen able to explain the difference.

   T8, Dirty → Clean, is the exception and the reason this is worth wiring: it is the one
   act with no task behind it. The room was never cleaned as far as this system is
   concerned; somebody with authority is saying it is fine. That is why it costs a reason,
   why W-122 grants it separately, and why W-124 records it.
*/
/** No transition code and no readiness — see the endpoint for why each is absent. */
export interface RoomMoved {
  roomId: string
  fromState: HousekeepingState
  toState: HousekeepingState
  effectiveAtUtc: string
}

/**
 * @param reason Required by the server for T4, T7 and T8 — see `transitionNeedsReason`.
 *  Sent as typed; the server refuses a blank one rather than the client guessing.
 */
export const moveRoom = (
  roomId: string,
  body: {
    toState: HousekeepingState
    actorUserId: string
    reason?: string
    capturedAtUtc?: string
  },
) => request<RoomMoved>(`/rooms/${roomId}/transition`, { method: 'POST', body })

/* ── WHAT HAPPENED TO THIS ROOM (Sprint 37) ────────────────────────────────────

   `RoomStatusHistory` has recorded every move a room has made since Sprint 1, and nothing
   read it for thirty-six sprints. This is its first reader.

   **IT IS NOT W-124.** The activity log carries acts of AUTHORITY — an override, a
   permission change — which is what an administrator asks for. A supervisor in a corridor
   asks "what happened to 110 today?", and the answer to that is mostly ordinary work, which
   W-124 deliberately does not carry. An override shows in both, and that is right: one is
   the room's story and one is the actor's.
*/

/** What happened, as a word. From `Wire.Of(TransitionCode)` — the same vocabulary the
 *  generated transition contract carries, so the panel and the matrix cannot disagree. */
export type RoomEvent =
  | 'STARTED' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED'
  | 'SOILED' | 'SOILED_BEFORE_INSPECTION'
  | 'CANCELLED' | 'OVERRIDDEN' | 'TOUCH_UP'

export interface RoomHistoryEntry {
  historyId: number
  event: RoomEvent
  fromState: HousekeepingState
  toState: HousekeepingState
  /** Null is a real answer: a move written by the property system was made by nobody. */
  actorName: string | null
  actorNameAr: string | null
  reason: string | null
  source: string
  /** When the person acted, on their device. */
  capturedAtUtc: string
  /** When the server applied it. The two differ only for a replayed offline action. */
  atUtc: string
}

export interface RoomHistoryView {
  roomId: string
  roomNumber: string
  /** The window the server actually used, which may not be the one that was asked for. */
  days: number
  truncated: boolean
  entries: RoomHistoryEntry[]
}

export const fetchRoomHistory = (
  roomId: string,
  options: { days?: number; signal?: AbortSignal } = {},
) => request<RoomHistoryView>(
  `/rooms/${roomId}/history${options.days ? `?days=${options.days}` : ''}`,
  options.signal ? { signal: options.signal } : {},
)

/* ── M-03: WHAT THE ROOM NEEDS, BEFORE THE DOOR IS OPENED (Sprint 40) ───────────

   Keyed by ROOM and not by task, because M-17 scans a door and a door knows no task.
   The room's open work comes back inside the view, and is null on a room with nothing
   outstanding — which is the answer a scan most needs to give plainly. */

/** One line of the standard: what a room of this type needs for this kind of clean. */
export interface RoomLinenLine {
  stockItemCode: string
  name: string
  nameAr: string | null
  /** "piece", "pair". Displayed, never calculated with. */
  unit: string
  quantity: number
}

export interface RoomTaskSummary {
  taskId: string
  taskTypeCode: string
  taskTypeName: string
  taskTypeNameAr: string | null
  state: TaskState
  /** What the task was RAISED at, not what the type is worth today. */
  credits: number
  estimatedMinutes: number
  firstStartedAtUtc: string | null
}

export interface RoomChecklistSummary {
  templateName: string
  templateNameAr: string | null
  versionNumber: number
  itemCount: number
  /**
   * TRUE when this task is already bound to this version; false when it is merely the
   * newest published one. Two different promises — see `IRoomDetailQuery` — and the
   * screen says "will follow" for one and "would follow" for the other.
   */
  pinned: boolean
}

export interface LastCleanSummary {
  completedAtUtc: string
  taskTypeCode: string
  /** Null where the assignment is gone. Both names set means a pair cleaned it. */
  byName: string | null
  byNameAr: string | null
  byNameTwo: string | null
  byNameTwoAr: string | null
  minutes: number | null
  /** NULL IS A REAL ANSWER: the room was not inspected. Not the same as failing. */
  passedFirstTime: boolean | null
}

export interface RoomDetailView {
  roomId: string
  roomNumber: string
  floorCode: string
  buildingCode: string
  buildingName: string
  buildingNameAr: string | null
  roomTypeCode: string
  roomTypeName: string
  roomTypeNameAr: string | null
  /**
   * HOW MANY PEOPLE THE ROOM SLEEPS. Sprint 49.
   *
   * Sent beside `adultCount`/`childCount` because neither number means anything without
   * the other: "three people" is an ordinary stay in a room for four and a mattress on
   * the floor in a room for two, and the man walking to the door is carrying different
   * things in the two cases. See `peopleInRoom` and `isOverCapacity`.
   */
  capacity: number
  bedConfiguration: string | null
  /** **Where the room is in its cleaning.** Added in Sprint 62 — W-21 is the first screen
   *  that had to answer "is 207 clean?", which is what the front desk is asked. */
  housekeeping: HousekeepingState
  occupancy: OccupancyState
  service: ServiceState
  /** Null is not "no arrival today" — it is "the property system has not said". */
  arrivalUtc: string | null
  departureUtc: string | null
  adultCount: number | null
  childCount: number | null
  vipFlag: boolean
  cleaningNotes: string | null
  /** Null on a room with nothing outstanding. */
  task: RoomTaskSummary | null
  checklist: RoomChecklistSummary | null
  linen: RoomLinenLine[]
  lastClean: LastCleanSummary | null
}

/**
 * **M-17 — SCAN A DOOR AND THE ROOM OPENS.**
 *
 * `scanned` is WHATEVER THE CAMERA READ, unprocessed — normally the URL a door sticker
 * encodes, sometimes a code somebody typed. Turning it into a token is the server's job
 * (`QrTokens.FromScan`), so there is one implementation of that rule rather than one per
 * client; this project's most repeated defect is a rule enforced in one of the two places
 * that state it.
 *
 * A POST, and the code is in the BODY. A door code is a capability, and a capability in a
 * query string is a capability in a proxy log and a browser history. The call still writes
 * nothing on the server — the scan is a shortcut and nothing about it is recorded.
 *
 * It returns the WHOLE room card, not an id: one round trip, because the moment a man is
 * holding a phone against a door is the moment his signal is worst.
 */
export const openRoomByDoorCode = (
  scanned: string,
  options: { signal?: AbortSignal } = {},
) => request<RoomDetailView>('/rooms/by-door-code', {
  method: 'POST',
  body: { scanned },
  ...(options.signal ? { signal: options.signal } : {}),
})

export const fetchRoomDetail = (
  roomId: string,
  options: { signal?: AbortSignal } = {},
) => request<RoomDetailView>(
  `/rooms/${roomId}/detail`,
  options.signal ? { signal: options.signal } : {},
)

/**
 * **CQ-138 — TAKE A ROOM OFF SALE, OR PUT IT BACK.** Sprint 52.
 *
 * `outOfService: true` withdraws it, `false` returns it, and the reason is mandatory both
 * ways — the server refuses without one, and this signature does not make it optional so
 * that nobody discovers the rule from a rejection.
 *
 * **IT IS NOT A ROOM TRANSITION AND DOES NOT GO THROUGH ONE.** Whether a room can be sold
 * and where it is in its cleaning are different dimensions; a room taken off sale is still
 * dirty or clean or inspected, and comes back exactly as it left.
 *
 * The server will refuse a room a FAULT took out of order (`room.isOutOfOrder`). That room
 * comes back by resolving the fault, which is a different permission and a different
 * screen, and the error message says so.
 */
export const setRoomService = (
  roomId: string,
  outOfService: boolean,
  reason: string,
  options: { signal?: AbortSignal } = {},
) => request<RoomServiceChanged>(`/rooms/${roomId}/service`, {
  method: 'POST',
  body: { outOfService, reason },
  ...(options.signal ? { signal: options.signal } : {}),
})

export interface RoomServiceChanged {
  roomId: string
  roomNumber: string
  outOfService: boolean
  /** Whether it can be sold now — the server's own recomputation across all three
   *  dimensions, so a room put back on sale while a guest is still in it says so. */
  isReady: boolean
}
